import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useEffect, useRef } from "react";
import { categoryKeys, documentTypeKeys, formulaKeys } from "@/hooks/useCategoriesQuery";
import { Category, DocumentType, Formula } from "@/contexts/TaskContext";
import type { FilterCounts } from "@/hooks/useFilterCounts";

export interface FooterItem {
  id: string;
  type: "text" | "link" | "button";
  content: string;
  url: string | null;
  sort_order: number;
}

interface BootstrapData {
  filterCounts: FilterCounts;
  categories: Category[];
  documentTypes: DocumentType[];
  formulas: Formula[];
  typewriterPhrases: string[];
  footerItems: FooterItem[];
}

const EMPTY_FILTER_COUNTS: FilterCounts = {
  byCategory: {},
  byYear: {},
  byDocType: {},
  byFormula: {},
  byTaskType: {},
  byVerb: {},
  totalWithYear: 0,
  totalWithDocType: 0,
  totalWithFormula: 0,
  totalWithTaskType: 0,
  totalWithVerb: 0,
  totalCount: 0,
  availableYears: [],
  availableTaskTypes: [],
  availableVerbs: [],
  availableCategories: [],
  availableDocTypes: [],
  availableFormulas: [],
};

function parseBootstrapResponse(raw: Record<string, any>): BootstrapData {
  const fc = raw.filterCounts || {};

  // Parse byYear keys from string to number
  const byYear: Record<number, number> = {};
  if (fc.byYear) {
    for (const [k, v] of Object.entries(fc.byYear)) {
      byYear[Number(k)] = v as number;
    }
  }

  const filterCounts: FilterCounts = {
    byCategory: (fc.byCategory || {}) as Record<string, number>,
    byYear,
    byDocType: (fc.byDocType || {}) as Record<string, number>,
    byFormula: (fc.byFormula || {}) as Record<string, number>,
    byTaskType: (fc.byTaskType || {}) as Record<string, number>,
    byVerb: (fc.byVerb || {}) as Record<string, number>,
    totalWithYear: fc.totalWithYear || 0,
    totalWithDocType: fc.totalWithDocType || 0,
    totalWithFormula: fc.totalWithFormula || 0,
    totalWithTaskType: fc.totalWithTaskType || 0,
    totalWithVerb: fc.totalWithVerb || 0,
    totalCount: fc.totalCount || 0,
    availableYears: (fc.availableYears || []) as number[],
    availableTaskTypes: (fc.availableTaskTypes || []) as string[],
    availableVerbs: (fc.availableVerbs || []) as string[],
    availableCategories: (fc.availableCategories || []) as string[],
    availableDocTypes: (fc.availableDocTypes || []) as string[],
    availableFormulas: (fc.availableFormulas || []) as string[],
  };

  // Sort categories with Polish locale
  const categories = ((raw.categories || []) as Category[]).sort((a, b) =>
    a.name.localeCompare(b.name, "pl", { sensitivity: "base" })
  );

  return {
    filterCounts,
    categories,
    documentTypes: (raw.documentTypes || []) as DocumentType[],
    formulas: (raw.formulas || []) as Formula[],
    typewriterPhrases: (raw.typewriterPhrases || []) as string[],
    footerItems: (raw.footerItems || []) as FooterItem[],
  };
}

/**
 * Bootstrap RPC — fetches categories, document types, formulas,
 * typewriter phrases, and filter counts in a single roundtrip.
 * Pre-populates React Query caches so individual hooks find data instantly.
 */
export function useBootstrapData() {
  const queryClient = useQueryClient();
  const seededRef = useRef(false);

  const { data, isLoading } = useQuery({
    queryKey: ["bootstrap-data"],
    queryFn: async () => {
      const { data, error } = await supabase.rpc("get_bootstrap_data");
      if (error) throw error;
      return parseBootstrapResponse((data as Record<string, any>) || {});
    },
    staleTime: 5 * 60_000,
    gcTime: 30 * 60_000,
  });

  // Seed individual caches once when bootstrap data arrives
  useEffect(() => {
    if (!data || seededRef.current) return;
    seededRef.current = true;

    queryClient.setQueryData(categoryKeys.lists(), data.categories);
    queryClient.setQueryData(documentTypeKeys.lists(), data.documentTypes);
    queryClient.setQueryData(formulaKeys.lists(), data.formulas);
    queryClient.setQueryData(["typewriter-phrases"], data.typewriterPhrases);
    queryClient.setQueryData(["filter-counts"], data.filterCounts);
    queryClient.setQueryData(["footer-items"], data.footerItems);
  }, [data, queryClient]);

  return {
    categories: data?.categories ?? [],
    documentTypes: data?.documentTypes ?? [],
    formulas: data?.formulas ?? [],
    filterCounts: data?.filterCounts ?? EMPTY_FILTER_COUNTS,
    footerItems: data?.footerItems ?? [],
    isLoading,
  };
}
